import type {FC} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import SearchIcon from '@mui/icons-material/Search'
import type {Conversation} from '@interfaces/alpinist/conversation'
import {aiCoreAPI, useUpdateConversationMutation} from '@apis/aiCoreAPI'
import {ALPINIST_STORAGE_KEYS} from '@shared/hooks/useAlpinistUrlSync'
import ConversationTitle from './ConversationTitle'
import ScheduledStatusIcon from './ScheduledStatusIcon'
import {safeShiftPaper} from '@containers/Dashboards/AgentChat/utils.ts'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'

const SCHEDULED = 'scheduled'

type FilterKey = 'all' | 'chat' | 'scheduled'

// The filter toggle drives the server-side category param, so paging + search
// stay correct (changing it starts a fresh paginated query).
const CATEGORY_BY_FILTER: Record<FilterKey, string> = {
  all: 'general,scheduled',
  chat: 'general',
  scheduled: SCHEDULED,
}

const isFilterKey = (v: string | null): v is FilterKey =>
  v === 'all' || v === 'chat' || v === 'scheduled'

interface Props {
  onClose: () => void
  /** Used only to decide whether to surface the scheduled filter toggle. */
  conversations: Conversation[]
  activeId?: string | null
  onSelect: (id: string) => void
  loading?: boolean
}

const ConversationSwitcher: FC<Props> = ({onClose, conversations, activeId, onSelect}) => {
  const [t] = useCustomTranslation('alpinist')
  // Persist the selected filter across reloads (sessionStorage, same scope as the
  // Alpinist panel/conversation state in useAlpinistUrlSync).
  const [filter, setFilterState] = useState<FilterKey>(() => {
    try {
      const stored = sessionStorage.getItem(ALPINIST_STORAGE_KEYS.filter)
      return isFilterKey(stored) ? stored : 'all'
    } catch {
      return 'all'
    }
  })
  const setFilter = useCallback((val: FilterKey) => {
    setFilterState(val)
    try {
      sessionStorage.setItem(ALPINIST_STORAGE_KEYS.filter, val)
    } catch {
      // best-effort persistence
    }
  }, [])
  const [searchInput, setSearchInput] = useState('')

  // Debounce the search term so we don't fire a request on every keystroke.
  const [search, setSearch] = useState('')
  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(id)
  }, [searchInput])

  // Separate "archived" view toggle (NOT a 4th filter tab): when on, the list
  // shows archived conversations (with an unarchive action) instead of active
  // ones. The category filter + search still apply on top.
  const [showArchived, setShowArchived] = useState(false)
  const [updateConversation] = useUpdateConversationMutation()
  const toggleArchive = useCallback(
    (c: Conversation) => updateConversation({id: c.id, archived: !c.archived}),
    [updateConversation],
  )

  const hasScheduled = useMemo(
    () => conversations.some((c) => c.category === SCHEDULED),
    [conversations],
  )

  const {data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage} =
    aiCoreAPI.endpoints.listConversationsInfinite.useInfiniteQuery({
      category: CATEGORY_BY_FILTER[filter],
      search,
      archived: showArchived || undefined,
    })

  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.results), [data])

  // Spinner in the search field while the debounce is pending or a search/filter
  // refetch is in flight (excludes the initial load and infinite-scroll paging).
  const searching =
    searchInput.trim() !== search || (isFetching && !isLoading && !isFetchingNextPage)

  // Infinite scroll: load the next page as the list nears the bottom.
  const scrollRef = useRef<HTMLUListElement | null>(null)
  const onScroll = (): void => {
    const el = scrollRef.current
    if (!el || !hasNextPage || isFetchingNextPage) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      void fetchNextPage()
    }
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <Box sx={{px: 1, pt: 1, display: 'flex', flexDirection: 'column', gap: 1}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <TextField
            size="small"
            fullWidth
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('conversations_search_placeholder')}
            sx={{'& .MuiOutlinedInput-input': {py: 0.3}}}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} color="inherit" />
                </InputAdornment>
              ) : null,
            }}
          />
          <Tooltip
            title={showArchived ? t('conversations_show_active') : t('conversations_show_archived')}
          >
            <IconButton
              size="small"
              color={showArchived ? 'primary' : 'default'}
              onClick={() => setShowArchived((v) => !v)}
              aria-label={
                showArchived ? t('conversations_show_active') : t('conversations_show_archived')
              }
              sx={{flexShrink: 0}}
            >
              <ArchiveOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {hasScheduled && (
          <ToggleButtonGroup
            size="small"
            color="primary"
            exclusive
            fullWidth
            value={filter}
            onChange={(_e, val: FilterKey | null) => val && setFilter(val)}
            aria-label={t('conversations_filter_all')}
            sx={{'& .MuiToggleButton-root': {py: 0.25, lineHeight: 1.4}}}
          >
            <ToggleButton value="all">{t('conversations_filter_all')}</ToggleButton>
            <ToggleButton value="chat">{t('conversations_filter_chats')}</ToggleButton>
            <ToggleButton value="scheduled">{t('conversations_filter_scheduled')}</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{p: 3, display: 'flex', alignItems: 'center', gap: 1}}>
          <CircularProgress size={18} />
          <Typography variant="body2">{t('conversations_loading')}</Typography>
        </Box>
      ) : (
        <List dense ref={scrollRef} onScroll={onScroll} sx={{flex: 1, overflowY: 'auto'}}>
          {items.map((c, index) => {
            const subtitle = c.modified ?? c.created ?? ''
            const selected = c.id === activeId
            const isEven = index % 2 === 0
            const isScheduled = c.category === SCHEDULED
            return (
              <ListItem
                key={c.id}
                disablePadding
                secondaryAction={
                  <Tooltip
                    title={showArchived ? t('conversations_unarchive') : t('conversations_archive')}
                  >
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label={
                        showArchived ? t('conversations_unarchive') : t('conversations_archive')
                      }
                      onClick={(e) => {
                        e.stopPropagation()
                        void toggleArchive(c)
                      }}
                      sx={{color: 'text.secondary'}}
                    >
                      {showArchived ? (
                        <UnarchiveOutlinedIcon fontSize="small" />
                      ) : (
                        <ArchiveOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                }
                sx={{
                  bgcolor: (theme) => (isEven ? safeShiftPaper(theme, 0.03, 0.06) : 'transparent'),
                  // Reveal the archive action only on row hover / keyboard focus
                  // (opacity, not display, so the layout never shifts).
                  '& .MuiListItemSecondaryAction-root': {
                    opacity: 0,
                    transition: 'opacity 0.15s ease',
                  },
                  '&:hover .MuiListItemSecondaryAction-root, &:focus-within .MuiListItemSecondaryAction-root':
                    {
                      opacity: 1,
                    },
                }}
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    onSelect(c.id)
                    onClose()
                  }}
                  sx={{
                    pr: 6,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0}}>
                        {isScheduled && <ScheduledStatusIcon outcome={c.scheduled_outcome} />}
                        <Typography variant="body2" noWrap sx={{minWidth: 0}}>
                          <ConversationTitle initialTitle={c.title} live={selected} />
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{display: 'block', color: 'text.disabled'}}
                      >
                        {new Date(subtitle).toLocaleString()}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )
          })}

          {!items.length && (
            <Box sx={{px: 2, py: 3}}>
              <Typography variant="body2" color="text.secondary">
                {showArchived
                  ? t('conversations_archived_empty')
                  : search
                    ? t('conversations_search_empty')
                    : t('conversations_empty')}
              </Typography>
            </Box>
          )}

          {isFetchingNextPage && (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
              <CircularProgress size={18} />
            </Box>
          )}
        </List>
      )}
    </Box>
  )
}

export default ConversationSwitcher
